// ============================================
// Memory Edit Module - Events (ES6)
// 이벤트 핸들러
// ============================================

import { showConfirm } from '../shared/confirm-modal.js';
import { handleAccordionToggle, updateAccordionHeight } from './accordion.js';
import { openEditSheet } from './edit-sheet.js';
import { getLayerData, isAccordionOpen, setLayerLoading, loadMoreItems } from './state.js';
import {
  loadLayerDataFromFirestore,
  updateSummary,
  createEvent,
  updateEvent,
  deleteEvent,
  createQuote,
  updateQuote,
  deleteQuote
} from './api.js';

// ============================================
// 이벤트 초기화
// ============================================

export function setupMemoryEditEvents(container, onDataChange) {
  if (!container) return;

  container.addEventListener('click', async (e) => {
    const actionEl = e.target.closest('[data-action]');
    if (!actionEl) return;

    const action = actionEl.dataset.action;
    const layer = actionEl.dataset.layer;
    const itemId = actionEl.dataset.id;

    switch (action) {
      case 'toggleAccordion':
        handleAccordionToggleWithLoad(layer, onDataChange);
        break;

      case 'toggleHistory':
        toggleHistoryContent(actionEl);
        break;

      case 'createMemory':
        handleCreateMemory(layer, onDataChange);
        break;

      case 'editMemory':
        handleEditMemory(layer, itemId, onDataChange);
        break;

      case 'deleteMemory':
        handleDeleteMemory(layer, itemId, onDataChange);
        break;

      case 'loadMore':
        loadMoreItems(layer);
        if (onDataChange) onDataChange();
        break;

      case 'editSummary':
        handleEditSummary(onDataChange);
        break;
    }
  });
}

// ============================================
// 아코디언 토글 + 데이터 로드
// ============================================

async function handleAccordionToggleWithLoad(layer, onDataChange) {
  handleAccordionToggle(layer, async (openedLayer) => {
    // 열릴 때 데이터 로드
    const data = getLayerData(openedLayer);
    if (!data || (Array.isArray(data) && data.length === 0)) {
      // 로딩 상태 설정 및 UI 업데이트
      setLayerLoading(openedLayer, true);
      if (onDataChange) onDataChange();

      await loadLayerDataFromFirestore(openedLayer);

      // 로딩 완료
      setLayerLoading(openedLayer, false);
      if (onDataChange) onDataChange();
    }
  });
}

// ============================================
// 히스토리 토글
// ============================================

function toggleHistoryContent(toggleEl) {
  const historyContainer = toggleEl.closest('.memory-layer-history');
  if (!historyContainer) return;

  const content = historyContainer.querySelector('.history-content');
  const arrow = toggleEl.querySelector('.history-toggle-arrow');

  if (content) {
    content.classList.toggle('collapsed');
  }
  if (arrow) {
    arrow.classList.toggle('rotated');
  }

  // 아코디언 높이 업데이트
  const layer = toggleEl.dataset.layer;
  if (layer) {
    updateAccordionHeight(layer);
  }
}

// ============================================
// 생성 핸들러
// ============================================

function handleCreateMemory(layer, onDataChange) {
  openEditSheet(layer, null, async (layer, data, isCreate, itemId) => {
    await handleSaveMemory(layer, data, isCreate, itemId);
    if (onDataChange) onDataChange();
  });
}

// ============================================
// 요약 편집 핸들러
// ============================================

function handleEditSummary(onDataChange) {
  const data = getLayerData('summary');
  openEditSheet('summary', data, async (layer, newData, isCreate, itemId) => {
    await handleSaveMemory(layer, newData, isCreate, itemId);
    if (onDataChange) onDataChange();
  });
}

// ============================================
// 편집 핸들러
// ============================================

function handleEditMemory(layer, itemId, onDataChange) {
  const data = getLayerData(layer);
  let item = null;

  if (Array.isArray(data)) {
    item = data.find(i => i.id === itemId);
  }

  if (!item) {
    console.warn('[MemoryEvents] 아이템을 찾을 수 없음:', layer, itemId);
    return;
  }

  openEditSheet(layer, item, async (layer, data, isCreate, itemId) => {
    await handleSaveMemory(layer, data, isCreate, itemId);
    if (onDataChange) onDataChange();
  });
}

// ============================================
// 삭제 핸들러
// ============================================

async function handleDeleteMemory(layer, itemId, onDataChange) {
  const confirmed = await showConfirm('정말 삭제하시겠습니까?', '삭제 확인');
  if (!confirmed) return;

  try {
    switch (layer) {
      case 'event':
        await deleteEvent(itemId);
        break;
      case 'quote':
        await deleteQuote(itemId);
        break;
    }

    if (onDataChange) onDataChange();
  } catch (error) {
    console.error('[MemoryEvents] 삭제 오류:', error);
  }
}

// ============================================
// 저장 핸들러
// ============================================

async function handleSaveMemory(layer, data, isCreate, itemId) {
  switch (layer) {
    case 'event':
      if (isCreate) {
        await createEvent(data);
      } else {
        await updateEvent(itemId, data);
      }
      break;

    case 'quote':
      if (isCreate) {
        await createQuote(data);
      } else {
        await updateQuote(itemId, data);
      }
      break;

    case 'summary':
      await updateSummary(data);
      break;
  }
}
